<template>
    <section class="pip-root">
        <div v-if="!product" class="pip-placeholder">
            <i class="fas fa-chart-line"></i>
            <p>목록에서 상품을 선택하면 원단위 결과를 조회할 수 있습니다.</p>
        </div>

        <template v-else>
            <div class="pip-table-shell">
                <div v-if="loading" class="pip-loading"><i class="fas fa-spinner fa-spin"></i> 불러오는 중</div>
                <div v-else-if="!rows.length" class="pip-empty-table">
                    조건에 해당하는 데이터가 없습니다. 스케줄 적재 전이거나 해당 기간에 데이터가 없을 수 있습니다.
                </div>
                <vue-good-table
                    v-else
                    :columns="tableColumns"
                    :rows="rows"
                    :fixed-header="true"
                    compactMode
                    :sort-options="{ enabled: true }"
                    class="pip-vgt pip-panel--vgt"
                >
                    <template slot="table-row" slot-scope="props">
                        <span v-if="props.column.field === 'periodKey'">{{ formatPeriod(props.row) }}</span>
                        <span v-else-if="props.column.field === 'calcAt'">{{ formatCalcAt(props.row.calcAt) }}</span>
                        <span v-else-if="props.column.field === 'energyMJ'">{{ formatNum(props.row.energyMJ) }}</span>
                        <span v-else-if="props.column.field === 'productionQty'">{{
                            formatNum(props.row.productionQty)
                        }}</span>
                        <span v-else-if="props.column.field === 'intensityValue'">{{
                            formatNum(props.row.intensityValue)
                        }}</span>
                        <span v-else-if="props.column.field === 'productCode'">{{
                            props.row.productCode != null && String(props.row.productCode).trim() !== ""
                                ? props.row.productCode
                                : "—"
                        }}</span>
                        <span v-else>{{ props.formattedRow[props.column.field] }}</span>
                    </template>
                </vue-good-table>
            </div>
        </template>
    </section>
</template>

<script>
export default {
    name: "ProductionIntensityPanel",
    props: {
        product: { type: Object, default: null },
        /** 상품 마스터 `unitSmallCode` — 생산량 열 헤더에 표시 */
        unitSmallCode: { type: String, default: "" },
        /** 상품 마스터 `targetEiUnit` — 원단위 열 헤더에 표시 */
        targetEiUnit: { type: String, default: "" },
        rows: { type: Array, default: () => [] },
        loading: { type: Boolean, default: false },
    },
    computed: {
        tableColumns() {
            const th = "vth text-center";
            const td = "vtd";
            const tdNum = "vtd text-right pip-td-num";
            const prodLabel = this.unitSmallCode ? `생산량 (${this.unitSmallCode})` : "생산량";
            const intLabel = this.targetEiUnit ? `원단위 (${this.targetEiUnit})` : "원단위";
            return [
                { label: "기준일", field: "periodKey", width: "108px", thClass: th, tdClass: `${td} text-center` },
                { label: "생산 관제점", field: "prodPtAddr", width: "140px", thClass: th, tdClass: td },
                { label: "상품코드", field: "productCode", width: "100px", thClass: th, tdClass: td },
                {
                    label: "에너지",
                    field: "energyKWh",
                    type: "decimal",
                    width: "110px",
                    thClass: th,
                    tdClass: tdNum,
                },
                {
                    label: prodLabel,
                    field: "productionQty",
                    type: "decimal",
                    width: "110px",
                    thClass: th,
                    tdClass: tdNum,
                },
                {
                    label: intLabel,
                    field: "intensityValue",
                    type: "decimal",
                    width: "100px",
                    thClass: th,
                    tdClass: tdNum,
                },
                { label: "산출 시각", field: "calcAt", width: "148px", thClass: th, tdClass: `${td} text-center` },
            ];
        },
    },
    methods: {
        formatPeriod(row) {
            const t = String(row.periodType || "D")
                .toUpperCase()
                .charAt(0);
            const k = row.periodKey;
            if (k == null) return "—";
            const s = String(k).replace(/\D/g, "");
            if (t === "M" && s.length >= 6) return `${s.slice(0, 4)}-${s.slice(4, 6)}`;
            if (s.length >= 8) return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
            return String(k);
        },
        formatCalcAt(v) {
            if (v == null || v === "") return "—";
            const d = v instanceof Date ? v : new Date(v);
            if (Number.isNaN(d.getTime())) return String(v);
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");
            const h = String(d.getHours()).padStart(2, "0");
            const min = String(d.getMinutes()).padStart(2, "0");
            return `${y}-${m}-${day} ${h}:${min}`;
        },
        formatNum(v) {
            if (v == null || v === "") return "—";
            const n = Number(v);
            return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 6 }) : String(v);
        },
    },
};
</script>

<style scoped>
.pip-root {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    padding: 12px 14px;
    min-width: 0;
}

.pip-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 8px;
    flex-shrink: 0;
}

.pip-title {
    margin: 0 0 3px;
    font-size: 14px;
    font-weight: 700;
    color: var(--puc-navy, #1b263b);
}

.pip-desc {
    margin: 0;
    font-size: 11px;
    line-height: 1.45;
    color: var(--puc-slate-500, #64748b);
    max-width: 680px;
}

.pip-product-tag {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
    flex-shrink: 0;
    padding: 5px 9px;
    border-radius: 8px;
    background: rgba(241, 245, 249, 0.95);
    border: 1px solid rgba(226, 232, 240, 0.96);
}

.pip-product-name {
    font-size: 12px;
    font-weight: 700;
    color: var(--puc-slate-800, #1e293b);
}

.pip-product-code {
    font-size: 10px;
    font-weight: 600;
    color: var(--puc-slate-500, #64748b);
}

.pip-placeholder {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 24px;
    color: var(--puc-slate-500, #64748b);
    font-size: 12px;
    font-weight: 600;
    text-align: center;
}

.pip-placeholder i {
    font-size: 28px;
    color: var(--puc-slate-300, #cbd5e1);
}

.pip-meta {
    font-size: 11px;
    font-weight: 700;
    color: var(--puc-slate-500, #64748b);
    margin-bottom: 6px;
    flex-shrink: 0;
}

.pip-table-shell {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    border: 1px solid rgba(226, 232, 240, 0.96);
    border-radius: 8px;
    background: #fff;
}

.pip-loading,
.pip-empty-table {
    padding: 20px;
    text-align: center;
    font-size: 12px;
    color: var(--puc-slate-500, #64748b);
}

.pip-vgt {
    flex: 1;
    min-height: 0;
}
</style>

<style>
.pip-panel--vgt table.vgt-table {
    border: none;
    font-size: 11px;
}

.pip-panel--vgt table.vgt-table thead th.vth {
    background: #1b263b;
    color: #fff;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    font-size: 10px;
    font-weight: 700;
    padding-top: 9px;
    padding-bottom: 9px;
}

.pip-panel--vgt table.vgt-table tbody td.vtd {
    padding: 7px 9px;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
    font-size: 11px;
}

.pip-panel--vgt table.vgt-table tbody tr:hover td.vtd {
    background: #f8fafc;
}

.pip-panel--vgt .pip-td-num {
    font-variant-numeric: tabular-nums;
}
</style>
